/**
 * バナー／LP の「スクロールで入ってきたら現れる」演出。
 *
 * ★ 隠すのは JS の仕事にする ★
 *   初期状態（透明・少し下）を CSS に直接書くと、JS が読めなかったときに
 *   **バナーが永久に消える**。属性を付けるのはこのファイルが動いたときだけなので、
 *   失敗しても素のまま見えている。CSS 側は
 *   [data-aicon-reveal='wait'] / [data-aicon-reveal='in'] にだけ効かせること。
 *
 * ★ ファーストビューの画像には付けない ★
 *   Shopify 公式（themes/best-practices/performance）に
 *   「Fade-in and reveal animations delay the LCP event even after the image
 *    finishes downloading」と明記がある。web.dev の LCP 解説でも
 *   「opacity が 0 で利用者に見えない要素」は LCP 候補から除外される。
 *   つまり出現演出を付けた画像は、ダウンロードが終わっても LCP として
 *   数えられず、Core Web Vitals が悪化する。
 *   → ヒーロー1枚目には data-aicon-reveal を付けない。
 *   → 加えてこのファイルは「読み込み時点ですでに画面内にある要素」を
 *     自分で除外する（下の isAboveTheFold）。上に置かれたバナーが
 *     一瞬消えてから出てくるチラつきも、これで同時に防げる。
 *
 * ★ 出るのは「利用者の画面を通過したとき」だけ ★
 *   時間で出してはいけない。読み込みから一定時間で出す作りにすると、
 *   スクロールして来る前に演出が終わってしまう（2026-08-26 に実際に発生）。
 *
 * ★ 動かすのは transform と opacity だけ ★
 *   web.dev の animations guide より。top / left で動かすと
 *   フレーム落ちが 1% → 50% に増える。この2つは合成段階だけで処理され、
 *   レイアウトもペイントも発生しない＝レイアウトずれ（CLS）も起きない。
 *
 * ★ 動きを減らす設定を尊重する ★
 *   prefers-reduced-motion: reduce のときは何もしない（＝最初から見えている）。
 *   WCAG 2.3.3「Animation from Interactions」の達成方法がこれ。
 *   前庭障害のある人はスクロール連動の動きで めまい・吐き気 が出ることがある。
 *
 * ★ 一度出したら監視をやめる ★
 *   往復スクロールのたびに出たり消えたりすると、読んでいる最中に
 *   ちらついて読めない。出現は1要素につき1回だけ。
 */

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

/** 読み込み時点で画面内（またはその少し下）にあるか。true なら演出しない。 */
const isAboveTheFold = (el) => el.getBoundingClientRect().top < window.innerHeight;

const setup = () => {
  // ★ 動きを減らす設定なら属性を一切付けない ★
  //   付けてから戻すのではなく最初から触らない。触ると一瞬でも透明になる。
  if (window.matchMedia && window.matchMedia(REDUCED_MOTION).matches) return;

  const targets = [...document.querySelectorAll('[data-aicon-reveal]')].filter(
    (el) => !isAboveTheFold(el),
  );
  if (targets.length === 0) return;

  // IntersectionObserver が無い環境では何もしない（＝素のまま見えている）
  if (!('IntersectionObserver' in window)) return;

  targets.forEach((el) => {
    el.dataset.aiconReveal = 'wait';
  });

  const io = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.dataset.aiconReveal = 'in';
        observer.unobserve(entry.target);
      });
    },
    {
      // 下端に少しかかった時点ではなく、きちんと入ってきてから出す。
      // 画面のいちばん下でチラッと動くのを避ける。
      rootMargin: '0px 0px -12% 0px',
      threshold: 0.12,
    },
  );

  targets.forEach((el) => io.observe(el));

  // ★ 保険は「もう画面に来ているもの」だけに限る（2026-08-26 修正）★
  //   もとは一定時間（4秒）で残り全部を無条件に出していた。そのため
  //   **利用者がスクロールして来る前に下のバナーの演出が終わっていた**
  //   （画面を開いて4秒後に、まだ見えていない面まで出てしまう）。
  //
  //   保険の目的は「バナーが永久に見えない」を避けることだけなので、
  //   **画面より下にあるものは触らない**。そこはスクロールで来たときに
  //   IntersectionObserver が出す。
  //
  //   ★ スクロールイベントは使わない ★
  //     assets/aicon-hero-banners.js と同じ方針。指で流している間ずっと
  //     発火して、そのたびに全要素の位置を測ることになる。
  //     観測が届かない唯一の現実的なケースは「裏タブで読み込まれた」ときで、
  //     これは表に戻った瞬間に IntersectionObserver 側が動きだす。
  //     取りこぼしが残るのは *表に戻った時点ですでに画面内にある面* だけなので、
  //     そこだけを掃く。
  const sweepVisible = () => {
    targets.forEach((el) => {
      if (el.dataset.aiconReveal !== 'wait') return;
      // 観測と同じ判定にする（rootMargin の下 -12% ぶんを見込む）
      if (el.getBoundingClientRect().top > window.innerHeight * 0.88) return;
      el.dataset.aiconReveal = 'in';
    });
  };

  if (document.hidden) {
    document.addEventListener('visibilitychange', function onShow() {
      if (document.hidden) return;
      document.removeEventListener('visibilitychange', onShow);
      // 表に戻った直後は観測がまだ動いていないことがある。1フレーム待って掃く。
      window.requestAnimationFrame(sweepVisible);
    });
  }
};

setup();
